/**
 * ContentGridItemState
 *
 * Renders the loading / error / empty state inside a grid's empty-list slot.
 * Used by pages that show a paginated grid of media (unlocked videos, content
 * tag results, etc.). The three branches are mutually exclusive:
 *
 *   isLoading        → centered loading indicator
 *   errorText set    → danger-colored message
 *   otherwise        → secondary-colored emptyText
 */

import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ContentGridItemStateProps {
  isLoading: boolean;
  errorText?: string;
  emptyText: string;
}

export const ContentGridItemState = ({ isLoading, errorText, emptyText }: ContentGridItemStateProps) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const padding = `${responsive.spacing['3xl']}px 0`;

  if (isLoading) {
    return (
      <div {...qaAttr({ id: 'content-grid-state', status: 'loading' })} className="flex justify-center" style={{ padding }}>
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  if (errorText) {
    return (
      <div {...qaAttr({ id: 'content-grid-state', status: 'error' })} className="text-center" style={{ padding }}>
        <p style={{ color: theme.danger }}>{errorText}</p>
      </div>
    );
  }

  return (
    <div {...qaAttr({ id: 'content-grid-state', status: 'empty' })} className="text-center" style={{ padding }}>
      <p style={{ color: theme.text.secondary }}>{emptyText}</p>
    </div>
  );
};
